const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const normalizeDatePart = (value: unknown, fallbackYear?: string): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  const isoMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) {
    return isoMatch[0];
  }

  const slashMatch = trimmed.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/);
  if (slashMatch) {
    const [, month, day, rawYear] = slashMatch;
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const printedAtMatch = trimmed.match(/\b(?:SUN|MON|TUE|WED|THU|FRI|SAT)?\s*(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{1,2})(?:[,\s]+(\d{2,4})(?!:))?\b/i);
  if (printedAtMatch) {
    const [, monthName, day, rawYear] = printedAtMatch;
    const monthIndex = MONTH_NAMES.indexOf(monthName.toUpperCase());
    const year = rawYear
      ? (rawYear.length === 2 ? `20${rawYear}` : rawYear)
      : fallbackYear;

    if (monthIndex >= 0 && year) {
      return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  return '';
};

export const normalizeTimePart = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  const isoTimeMatch = trimmed.match(/[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  const match = isoTimeMatch || trimmed.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/);
  if (!match) {
    return '';
  }

  const [, hours, minutes, seconds] = match;
  return `${hours.padStart(2, '0')}:${minutes}:${seconds || '00'}`;
};

export const subtractOneDay = (datePart: string): string => {
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return '';
  }

  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};

const getFallbackYear = (details: any): string => (
  normalizeDatePart(details?.shift_date).slice(0, 4)
  || normalizeDatePart(details?.created_at).slice(0, 4)
);

export const resolveVendingSalesReportDate = (finalJson: any, details: any): string => {
  const fallbackYear = getFallbackYear(details);
  const explicitSalesDate = normalizeDatePart(details?.report_date, fallbackYear)
    || normalizeDatePart(details?.parsed_data?.report_date, fallbackYear)
    || normalizeDatePart(details?.parsed_data?.shift_date, fallbackYear)
    || normalizeDatePart(finalJson?.report_date, fallbackYear)
    || normalizeDatePart(finalJson?.sales_date, fallbackYear)
    || normalizeDatePart(finalJson?.shift_date, fallbackYear);
  if (explicitSalesDate) {
    return explicitSalesDate;
  }

  const printedDate = normalizeDatePart(finalJson?.printed_at, fallbackYear)
    || normalizeDatePart(finalJson?.date, fallbackYear)
    || normalizeDatePart(details?.created_at);
  return printedDate ? subtractOneDay(printedDate) : '';
};

export const resolveVendingInventoryReportDate = (finalJson: any, details: any): string => {
  const fallbackYear = getFallbackYear(details);
  return normalizeDatePart(details?.parsed_data?.report_date, fallbackYear)
    || normalizeDatePart(details?.parsed_data?.shift_date, fallbackYear)
    || normalizeDatePart(finalJson?.report_date, fallbackYear)
    || normalizeDatePart(finalJson?.shift_date, fallbackYear)
    || normalizeDatePart(details?.report_date, fallbackYear)
    || normalizeDatePart(finalJson?.printed_at, fallbackYear)
    || normalizeDatePart(finalJson?.date, fallbackYear)
    || normalizeDatePart(details?.shift_date, fallbackYear)
    || normalizeDatePart(details?.created_at);
};

export const resolveVendingInventoryReportTime = (finalJson: any, details: any): string => (
  normalizeTimePart(details?.parsed_data?.report_time)
  || normalizeTimePart(details?.parsed_data?.shift_time)
  || normalizeTimePart(finalJson?.report_time)
  || normalizeTimePart(finalJson?.shift_time)
  || normalizeTimePart(details?.report_time)
  || normalizeTimePart(finalJson?.printed_at)
  || normalizeTimePart(finalJson?.time)
  || normalizeTimePart(details?.shift_start_time)
);

export const resolveNonTicketReportDate = (
  reportType: string,
  finalJson: any,
  details: any,
): string => {
  if (reportType === 'vending_shift_report') {
    const fallbackYear = getFallbackYear(details);
    return normalizeDatePart(finalJson?.to_datetime, fallbackYear)
      || normalizeDatePart(details?.parsed_data?.report_date, fallbackYear)
      || normalizeDatePart(details?.parsed_data?.shift_date, fallbackYear)
      || normalizeDatePart(finalJson?.report_date, fallbackYear)
      || normalizeDatePart(finalJson?.shift_date, fallbackYear)
      || normalizeDatePart(details?.report_date, fallbackYear)
      || normalizeDatePart(details?.shift_date, fallbackYear)
      || normalizeDatePart(details?.created_at);
  }

  if (reportType === 'vending_sales') {
    return resolveVendingSalesReportDate(finalJson, details);
  }

  if (reportType === 'vending_inventory') {
    return resolveVendingInventoryReportDate(finalJson, details);
  }

  const fallbackYear = getFallbackYear(details);
  return normalizeDatePart(details?.parsed_data?.report_date, fallbackYear)
    || normalizeDatePart(details?.parsed_data?.shift_date, fallbackYear)
    || normalizeDatePart(finalJson?.report_date, fallbackYear)
    || normalizeDatePart(finalJson?.date, fallbackYear)
    || normalizeDatePart(finalJson?.shift_date, fallbackYear)
    || normalizeDatePart(finalJson?.sales_date, fallbackYear)
    || normalizeDatePart(details?.report_date, fallbackYear)
    || normalizeDatePart(details?.shift_date, fallbackYear)
    || normalizeDatePart(finalJson?.printed_at, fallbackYear)
    || normalizeDatePart(details?.created_at);
};

export const resolveNonTicketReportTime = (
  reportType: string,
  finalJson: any,
  details: any,
): string => {
  if (reportType === 'vending_shift_report') {
    return normalizeTimePart(finalJson?.to_datetime)
      || normalizeTimePart(details?.parsed_data?.report_time)
      || normalizeTimePart(details?.parsed_data?.shift_time)
      || normalizeTimePart(finalJson?.report_time)
      || normalizeTimePart(finalJson?.shift_time)
      || normalizeTimePart(details?.report_time)
      || normalizeTimePart(details?.shift_start_time);
  }

  if (reportType === 'vending_inventory') {
    return resolveVendingInventoryReportTime(finalJson, details);
  }

  return normalizeTimePart(details?.parsed_data?.report_time)
    || normalizeTimePart(details?.parsed_data?.shift_time)
    || normalizeTimePart(finalJson?.report_time)
    || normalizeTimePart(finalJson?.shift_time)
    || normalizeTimePart(details?.report_time)
    || normalizeTimePart(finalJson?.time)
    || normalizeTimePart(details?.shift_start_time)
    || normalizeTimePart(finalJson?.printed_at);
};
